import { createQueryParamString } from '../utilities';
import {
  CreateRunPayload,
  CreateWorkspacePayload,
  AnalysisResponse,
  WindowService,
  WorkspaceListResponse,
  WorkspaceResponse,
  trulyHasData,
  PollyCommonRunsApiService,
  PollyWorkspaceCommonApiService
} from 'polly-commons';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { mergeMap } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import {
  SageMakerNotebookPayload,
  CreateNotebookInterface,
  SageMakerNotebookResponse,
  SageMakerURLResponse,
  StringFormAttribute,
  AnalysisPayload,
  ComputeAnalysisResponse,
  LaunchAnalysisResponse,
} from '../../../polly-manage.interfaces';
import { PollyManageBackendService } from '../../../@core/services';
import { ApiEndPoints } from '../../../polly-manage.constants';
import { createNotebookPayload } from '../../../@core/utilities/polly-manage.utilities';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class PollyManageAppLaunchService {
  constructor(
    private readonly pollyWorkspaceApiService: PollyWorkspaceCommonApiService,
    private readonly pollyCommonRunsApiService: PollyCommonRunsApiService,
    private readonly windowService: WindowService,
    private readonly backendService: PollyManageBackendService,
    private readonly router: Router
  ) {}

  public getWorkspaces(): Observable<WorkspaceListResponse> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('page[size]', '1000');
    return this.pollyWorkspaceApiService.list(httpParams);
  }

  public createWorkspace(name: string, description: string = ''): Observable<WorkspaceResponse> {
    const payload: CreateWorkspacePayload = {
      data: {
        type: 'workspaces',
        attributes: {
          name,
          description,
          project_property: {
            type: 'workspaces',
            labels: '',
          },
        },
      },
    };
    return this.pollyWorkspaceApiService.post(payload);
  }

  public createRun(workspaceId: number, componentId: number, name: string): Observable<AnalysisResponse> {
    const payload: CreateRunPayload = {
      data: {
        type: 'runs',
        attributes: {
          component_id: componentId.toString(),
          name,
        },
      },
    };
    let httpParams: HttpParams = new HttpParams().set('workspace_id', workspaceId);
    return this.pollyCommonRunsApiService.post(`compute/analysis`, payload, httpParams);
  }

  public openSageMakerApp(details: CreateNotebookInterface): Observable<SageMakerNotebookResponse> {
    const payload: SageMakerNotebookPayload = createNotebookPayload(details);
    return this.backendService
      .postReq(`${ApiEndPoints.GET_PROJECTS_ENDPOINT}/${details.workspace_id}/notebook`, payload)
      .pipe<SageMakerNotebookResponse>(trulyHasData)
      .pipe(
        mergeMap((resp: SageMakerNotebookResponse) => {
          if (!!resp.data.attributes && resp.data.attributes !== null) {
            const { estimated_time, instance_name } = resp.data.attributes;
            const sagemakerLink = this.router.serializeUrl(
              this.router.createUrlTree(['manage/jupyter-notebook'], {
                queryParams: {
                  notebook_name: details.notebook_name,
                  workspace_id: details.workspace_id,
                  instance_name,
                  estimated_time,
                },
              })
            );
            window.open(sagemakerLink, '_blank');
            return of(resp);
          } else {
            return throwError(new Error('Instance Creation Failed'));
          }
        })
      );
  }

  public getSageMakerPresignedUrl(workspaceId: string, instanceName: string): Observable<SageMakerURLResponse> {
    return this.backendService.getReq(`${ApiEndPoints.GET_PROJECTS_ENDPOINT}/${workspaceId}/notebook/${instanceName}`);
  }

  /**
   * this function open the Analysis redirect URL. Repository(Id/Name) and Dataset (Id) are Discover specific
   * @param redirectUrl URL you got from Analysis Restore response
   * @param repositoryId Repository Id
   * @param repositoryName Repository Name
   * @param datasetId Dataset Id
   */
  public openRunWfRequestApp(
    redirectUrl: string,
    repositoryId?: string,
    repositoryName?: string,
    datasetId?: string,
    launchSource?: string,
    projectId?: string,
    client?: StringFormAttribute
  ): void {
    let queryParam: string = createQueryParamString({
      repo_id: repositoryId,
      repo_name: repositoryName,
      dataset_id: datasetId,
      initiator: launchSource === 'atlas' ? 'omixatlas' : '',
      workspaceId: projectId,
      client_org_id: client?.value ?? undefined,
    });
    if (redirectUrl.includes('?')) queryParam = queryParam.replace('?', '&');
    this.windowService.openInNewTab(redirectUrl + queryParam);
  }

  public createAnalysisPostRun(
    workspaceId: number,
    app_id: string,
    name: string,
    client_id: string | null,
    repo_id?: string,
    dataset_id?: string
  ): Observable<ComputeAnalysisResponse> {
    const payload: AnalysisPayload = {
      data: {
        type: 'analysis',
        attributes: {
          app_id,
          name,
          ...(client_id && { client_org_id: client_id }),
          ...(repo_id && dataset_id && {
              analysis_info: {
                repo_id,
                dataset_id,
              },
            }),
        },
      },
    };
    return this.pollyCommonRunsApiService.post<AnalysisPayload, ComputeAnalysisResponse>(`compute/${workspaceId}/analysis`, payload);
  }

  public getLaunchAnalysisAppUrl(analysisId: number, workspaceId: number): Observable<LaunchAnalysisResponse> {
    return this.pollyCommonRunsApiService.get<LaunchAnalysisResponse>(`/compute/${workspaceId}/analysis/${analysisId}?action=launch`);
  }

  /**
   * this function open the Analysis redirect URL. Repository(Id/Name) and Dataset (Id) are Discover specific
   * @param redirectUrl URL you got from Analysis Restore response
   * @param repositoryId Repository Id
   * @param repositoryName Repository Name
   * @param datasetId Dataset Id
   */
  public openRunRequestAppWithURL(
    proxyPath: string,
    appId: string,
    sessionId: string,
    projectId?: string,
    analysis_id?: string,
    repositoryId?: string,
    repositoryName?: string,
    datasetId?: string,
  ): void {
    const redirectUrl = `runs/${sessionId}/${encodeURIComponent(proxyPath)}/app`;

    // Build query string
    let queryParam: string = createQueryParamString({
      repo_id: repositoryId,
      repo_name: repositoryName,
      dataset_id: datasetId,
      workspaceId: projectId,
      analysis_id,
      app_id: appId,
    });

    this.windowService.openInNewTab(redirectUrl + queryParam);
  }
}
